import React, { useState, useEffect } from "react";
import api from "../api/axios";
import { useNavigate, useParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const QuotationForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [savedQuotationData, setSavedQuotationData] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const [quotation, setQuotation] = useState({
    inquiry_id: "", // Select from existing inquiries
    customer_name: "",
    staff_id: "",
    branch_id: "",
    status: "Draft",
    discount_type: "amount",
    discount_value: 0,
    tax_percentage: 0,
    services: [
      {
        service_name: "air_ticket", // Default option
        description: "",
        quantity: 1,
        unit_price: 0,
        total_price: 0,
      },
    ],
  });

  const [branches, setBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [inquiries, setInquiries] = useState([]);

  const itemTypes = [
    { value: "air_ticket", label: "Air Ticket" },
    { value: "hotel", label: "Hotel" },
    { value: "visa", label: "Visa" },
    { value: "insurance", label: "Insurance" },
    { value: "baggage", label: "Baggage" },
    { value: "visa_message", label: "Visa Message" },
    { value: "ticketing", label: "Ticketing" },
    { value: "real_estate", label: "Real Estate" },
    { value: "visa_assistance", label: "Visa Assistance" },
    { value: "attractions", label: "Attractions" },
    { value: "umrah_service", label: "Umrah Service" },
    { value: "b2b_khasab", label: "B2B Khasab" },
    { value: "security_deposit", label: "Security Deposit" },
    { value: "typing_service", label: "Typing Service" },
    { value: "burj_khalifa", label: "Burj Khalifa" },
    { value: "desert_safari", label: "Desert Safari" },
    { value: "seat_pre_book", label: "Seat Pre-booking" },
    { value: "food", label: "Food" },
    { value: "others", label: "Others" },
  ];

  useEffect(() => {
    fetchBranches();
    fetchStaff();
    fetchInquiries();
    if (id) fetchQuotation();
  }, [id]);

  const fetchBranches = async () => {
    const res = await api.get("branches/");
    setBranches(res.data);
  };

  const fetchStaff = async () => {
    const res = await api.get("staff/");
    setStaff(res.data);
  };

  const fetchQuotation = async () => {
    const res = await api.get(`quotations/${id}/`);
    setQuotation(res.data);
  };

  const fetchInquiries = async () => {
    try {
      const res = await api.get("inquiries/");
      setInquiries(res.data);
    } catch (err) {
      console.error("Error fetching inquiries:", err);
    }
  };

  const handleChange = (e, index = null) => {
    const { name, value } = e.target;

    if (index !== null) {
      const services = [...quotation.services];
      services[index][name] =
        name === "quantity" || name === "unit_price"
          ? Number(value)
          : value;

      services[index].total_price =
        services[index].quantity * services[index].unit_price;

      setQuotation({ ...quotation, services });
    } else {
      setQuotation({
        ...quotation,
        [name]:
          name.includes("id") ||
          name === "discount_value" ||
          name === "tax_percentage"
            ? Number(value)
            : value,
      });
    }
  };

  const addService = () => {
    setQuotation({
      ...quotation,
      services: [
        ...quotation.services,
        {
          service_name: "air_ticket",
          description: "",
          quantity: 1,
          unit_price: 0,
          total_price: 0,
        },
      ],
    });
  };

  const removeService = (index) => {
    setQuotation({
      ...quotation,
      services: quotation.services.filter((_, i) => i !== index),
    });
  };

  const subtotal = quotation.services.reduce(
    (sum, s) => sum + s.total_price,
    0
  );

  const discountAmount =
    quotation.discount_type === "percentage"
      ? (subtotal * quotation.discount_value) / 100
      : quotation.discount_value;

  const taxableAmount = subtotal - discountAmount;
  const taxAmount = (taxableAmount * quotation.tax_percentage) / 100;
  const totalAmount = taxableAmount + taxAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        ...quotation,
        inquiry_id: Number(quotation.inquiry_id),
        staff_id: Number(quotation.staff_id),
        branch_id: Number(quotation.branch_id),
        discount_value: Number(quotation.discount_value),
        tax_percentage: Number(quotation.tax_percentage),
        services: quotation.services.map((s) => ({
          ...s,
          quantity: Number(s.quantity),
          unit_price: Number(s.unit_price),
          total_price: Number(s.total_price),
        })),
      };

      let result;
      if (id) {
        result = await api.put(`quotations/${id}/`, payload);
      } else {
        result = await api.post("quotations/", payload);
      }

      setSavedQuotationData({ ...payload, id: result.data?.id || id });
      setShowSuccess(true);
    } catch (err) {
      console.error("Error saving quotation:", err);
      alert("Failed to save quotation.");
    }
  };

  const handleDownloadPDF = async () => {
    if (!savedQuotationData) return;

    const doc = new jsPDF();
    const themeColor = [0, 128, 128]; // Teal

    // Add Logo
    try {
      const logoUrl = process.env.PUBLIC_URL + "/rekonic-logo.jpg";
      const logoImg = await new Promise((resolve) => {
        const img = new Image();
        img.src = logoUrl;
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });
      if (logoImg) {
        doc.addImage(logoImg, "JPEG", 15, 10, 50, 20);
      }
    } catch (e) {
      console.error("Logo failed to load", e);
    }

    doc.setFontSize(22);
    doc.setTextColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.text("QUOTATION", 195, 25, { align: "right" });

    doc.setDrawColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.setLineWidth(1);
    doc.line(15, 35, 195, 35);

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, "bold");
    doc.text("Quotation Details:", 15, 45);
    doc.setFont(undefined, "normal");
    doc.text(`ID: ${savedQuotationData.id || "N/A"}`, 15, 52);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 15, 59);
    doc.text(`Status: ${savedQuotationData.status || "Draft"}`, 15, 66);

    doc.setFont(undefined, "bold");
    doc.text("Bill To:", 130, 45);
    doc.setFont(undefined, "normal");
    doc.text(`${savedQuotationData.customer_name}`, 130, 52);

    // Table
    const tableRows = savedQuotationData.services.map((s) => [
      s.service_name || "-",
      s.quantity.toString(),
      s.unit_price.toFixed(2),
      s.total_price.toFixed(2),
    ]);

    autoTable(doc, {
      startY: 75,
      head: [["Service Name", "Qty", "Unit Price", "Total"]],
      body: tableRows,
      headStyles: { fillStyle: "F", fillColor: themeColor, textColor: 255 },
      alternateRowStyles: { fillColor: [240, 250, 250] },
      margin: { left: 15, right: 15 },
    });

    const finalY = doc.lastAutoTable.finalY + 10;

    // Totals
    const subtotal = savedQuotationData.services.reduce((sum, s) => sum + s.total_price, 0);
    const discountAmount = savedQuotationData.discount_type === "percentage" 
      ? (subtotal * savedQuotationData.discount_value) / 100 
      : savedQuotationData.discount_value;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * savedQuotationData.tax_percentage) / 100;
    const totalAmount = taxableAmount + taxAmount;

    doc.setFont(undefined, "bold");
    doc.text(`Subtotal:`, 140, finalY);
    doc.setFont(undefined, "normal");
    doc.text(`${subtotal.toFixed(2)}`, 195, finalY, { align: "right" });

    let currentY = finalY + 7;
    if (savedQuotationData.discount_value > 0) {
      doc.setFont(undefined, "bold");
      doc.text(`Discount:`, 140, currentY);
      doc.setFont(undefined, "normal");
      doc.text(`${discountAmount.toFixed(2)}`, 195, currentY, { align: "right" });
      currentY += 7;
    }
    if (savedQuotationData.tax_percentage > 0) {
      doc.setFont(undefined, "bold");
      doc.text(`Tax (${savedQuotationData.tax_percentage}%):`, 140, currentY);
      doc.setFont(undefined, "normal");
      doc.text(`${taxAmount.toFixed(2)}`, 195, currentY, { align: "right" });
      currentY += 7;
    }

    doc.setFillColor(themeColor[0], themeColor[1], themeColor[2]);
    doc.rect(135, currentY, 60, 10, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.text(`Grand Total:`, 140, currentY + 7);
    doc.text(`${totalAmount.toFixed(2)}`, 190, currentY + 7, { align: "right" });

    // Stamp
    try {
      const stampUrl = process.env.PUBLIC_URL + "/stamp.jpg";
      const stampImg = await new Promise((resolve) => {
        const img = new Image();
        img.src = stampUrl;
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
      });
      if (stampImg) {
        doc.addImage(stampImg, "JPEG", 20, currentY + 20, 30, 30);
      }
    } catch (e) {
      console.error("Stamp failed to load", e);
    }

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text("Authorized Signature", 20, currentY + 55);

    doc.save(`Quotation_${savedQuotationData.id || "New"}.pdf`);
  };

    // Render modal inline instead of returning early


  return (
    <div className="container mt-4 position-relative">
      {/* Success Modal Popup */}
      {showSuccess && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content text-center p-4">
              <h3 className="text-success mb-3">Quotation Saved Successfully!</h3>
              <p>What would you like to do next?</p>
              <div className="d-flex justify-content-center gap-3 mt-3">
                <button className="btn btn-primary" onClick={handleDownloadPDF}>
                  📄 View / Save PDF
                </button>
                <button className="btn btn-secondary" onClick={() => navigate("/dashboard/quotations/list")}>
                  Go to List
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>{id ? "Edit" : "New"} Quotation</h3>
        <button className="btn btn-secondary" onClick={() => navigate("/dashboard")}>
          Back to Dashboard
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Inquiry */}
        <div className="col-md-4 mb-3">
          <label className="form-label">Select Inquiry *</label>
          <select
            name="inquiry_id"
            value={quotation.inquiry_id}
            onChange={handleChange}
            className="form-select"
            required
          >
            <option value="">-- Select Inquiry --</option>
            {inquiries.map((inq) => (
              <option key={inq.id} value={inq.id}>
                Inquiry #{inq.id}
              </option>
            ))}
          </select>
        </div>

        {/* Customer */}
        <div className="mb-3">
          <label>Customer Name</label>
          <input
            type="text"
            name="customer_name"
            value={quotation.customer_name}
            onChange={handleChange}
            className="form-control"
            required
          />
        </div>

        {/* Branch */}
        <div className="mb-3">
          <label>Branch</label>
          <select
            name="branch_id"
            value={quotation.branch_id}
            onChange={handleChange}
            className="form-select"
            required
          >
            <option value="">Select Branch</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {/* Staff */}
        <div className="mb-3">
          <label>Staff</label>
          <select
            name="staff_id"
            value={quotation.staff_id}
            onChange={handleChange}
            className="form-select"
            required
          >
            <option value="">Select Staff</option>
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {/* Services */}
        <h5>Services</h5>
        {quotation.services.map((s, index) => (
          <div key={index} className="border p-3 mb-2 bg-light rounded">
            <select
              className="form-select mb-2"
              name="service_name"
              value={s.service_name}
              onChange={(e) => handleChange(e, index)}
            >
              {itemTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>

            <input
              className="form-control mb-2"
              placeholder="Description"
              name="description"
              value={s.description}
              onChange={(e) => handleChange(e, index)}
            />

            <div className="row">
              <div className="col">
                <input
                  type="number"
                  className="form-control"
                  name="quantity"
                  value={s.quantity}
                  onChange={(e) => handleChange(e, index)}
                />
              </div>
              <div className="col">
                <input
                  type="number"
                  className="form-control"
                  name="unit_price"
                  value={s.unit_price}
                  onChange={(e) => handleChange(e, index)}
                />
              </div>
              <div className="col">
                <input
                  type="number"
                  className="form-control"
                  value={s.total_price}
                  readOnly
                />
              </div>
            </div>

            <button
              type="button"
              className="btn btn-danger btn-sm mt-2"
              onClick={() => removeService(index)}
            >
              Remove
            </button>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-secondary mb-3"
          onClick={addService}
        >
          Add Service
        </button>

        <hr />

        {/* Discount / Tax */}
        <div className="row mb-3">
          <div className="col">
            <label>Discount Type</label>
            <select
              name="discount_type"
              value={quotation.discount_type}
              onChange={handleChange}
              className="form-select"
            >
              <option value="amount">Amount</option>
              <option value="percentage">Percentage</option>
            </select>
          </div>

          <div className="col">
            <label>Discount Value</label>
            <input
              type="number"
              name="discount_value"
              value={quotation.discount_value}
              onChange={handleChange}
              className="form-control"
            />
          </div>

          <div className="col">
            <label>Tax %</label>
            <input
              type="number"
              name="tax_percentage"
              value={quotation.tax_percentage}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>

        {/* Totals */}
        <div className="mb-3">
          <strong>Subtotal:</strong> {subtotal.toFixed(2)} <br />
          <strong>Tax:</strong> {taxAmount.toFixed(2)} <br />
          <strong>Total:</strong> {totalAmount.toFixed(2)}
        </div>

        <button type="submit" className="btn btn-primary">
          {id ? "Update" : "Save"} Quotation
        </button>
      </form>
    </div>
  );
};

export default QuotationForm;
